import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
} from '@mui/material';
import { X, Check } from '@phosphor-icons/react';
import { useParams } from 'react-router';
import { Api } from '../../api';
import { toast } from 'sonner';
import { useInvalidation } from 'api-read-hook';
import { SoloLoading } from '..';
import useApiCallable from '../../api/use-api-callable';

type Props = {
  open?: boolean;
  onClose?: () => void;
  userId?: string;
};

export default function EditContentTagsModal({
  open = true,
  onClose,
  userId,
}: Props) {
  const params = useParams();
  const id = userId || params.id || '';
  if (!id) return null;
  return <Modal id={id} open={open} onClose={onClose} />;
}

function Modal({
  id,
  open = true,
  onClose,
}: {
  id: string;
  open?: boolean;
  onClose?: () => void;
}) {
  const { invalidateExact } = useInvalidation();
  const callApi = useApiCallable();

  const statsApi = Api.UserStats.useReadItemStats(id);
  const tagsApi = Api.Tag.useReadCollectionAll({
    'order[category.name]': 'asc',
  });
  const categoriesApi = Api.TagCategory.useReadCollectionAll();

  const [selectedTagIds, setSelectedTagIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (statsApi.data && !initialized) {
      const currentTagIds = new Set(
        statsApi.data.user.contentTags?.map((t) => t['@id']) || [],
      );
      setSelectedTagIds(currentTagIds);
      setInitialized(true);
    }
  }, [statsApi.data, initialized]);

  const tagsByCategory = useMemo(() => {
    if (!tagsApi.data || !categoriesApi.data) return {};

    const grouped: { [key: string]: Api.Tag.V[] } = {};

    categoriesApi.data.member.forEach((category) => {
      grouped[category.name] = [];
    });

    tagsApi.data.member.forEach((tag) => {
      const categoryName = tag.category?.name || 'Other';
      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(tag);
    });

    return grouped;
  }, [tagsApi.data, categoriesApi.data]);

  if (statsApi.error || tagsApi.error || categoriesApi.error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Edit Content Tags</Typography>
            <IconButton onClick={onClose} size="small">
              <X size={20} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography color="error">Error loading data</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (!statsApi.data || !tagsApi.data || !categoriesApi.data) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">Edit Content Tags</Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SoloLoading />
        </DialogContent>
      </Dialog>
    );
  }

  const handleTagToggle = (tagId: string) => {
    setSelectedTagIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    const selectedTags = Array.from(selectedTagIds);

    const res = await callApi(`/users/${id}/as-admin`, {
      method: 'PATCH',
      jsonBody: {
        email: statsApi.data?.user.email || '',
        contentTags: selectedTags,
      },
    });

    if (res) {
      invalidateExact(`/users/${id}/stats`);
      toast.success('Content tags updated');
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Edit Content Tags</Typography>
          <IconButton onClick={onClose}>
            <X size={24} />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please select or deselect the corresponding tags
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {Object.entries(tagsByCategory).map(([categoryName, tags]) => (
            <Box
              key={categoryName}
              sx={{
                bgcolor: 'var(--mui-palette-background-level1)',
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                {categoryName}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {tags.map((tag) => {
                  const isSelected = selectedTagIds.has(tag['@id']);
                  return (
                    <Box
                      key={tag.id}
                      onClick={() => handleTagToggle(tag['@id'])}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: isSelected ? '#2D556C' : '#E0E0E0',
                        color: isSelected ? 'white' : 'text.primary',
                        cursor: 'pointer',
                        mb: 1,
                        mr: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '4px',
                          border: isSelected
                            ? '2px solid white'
                            : '2px solid #667085',
                          backgroundColor: isSelected ? 'white' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {isSelected && (
                          <Check
                            size={16}
                            color="var(--mui-palette-primary-main)"
                            weight="bold"
                          />
                        )}
                      </Box>
                      <Typography variant="body2">
                        {tag.internalName}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
